import { ArrowDownIcon, ArrowsUpDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactNode, useEffect, useState } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

import { Button } from './button';

interface SortableColumnHeaderProps<TData, TValue> {
  column: Column<TData, TValue>;
  headerTitle: string;
}

export function SortableColumnHeader<TData, TValue>({
  column,
  headerTitle,
}: SortableColumnHeaderProps<TData, TValue>) {
  return (
    <Button
      variant="ghost"
      className="group relative p-0 hover:bg-transparent"
      onClick={() =>
        column.getIsSorted() === 'desc'
          ? column.clearSorting()
          : column.toggleSorting(column.getIsSorted() === 'asc')
      }
    >
      {headerTitle}
      {!column.getIsSorted() && (
        <ArrowsUpDownIcon className="invisible ml-2 h-4 w-4 group-hover:visible" />
      )}
      {column.getIsSorted() &&
        (column.getIsSorted() === 'asc' ? (
          <ArrowDownIcon className="ml-2 h-4 w-4" />
        ) : (
          <ArrowUpIcon className="ml-2 h-4 w-4" />
        ))}
    </Button>
  );
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  paginate?: boolean;
  paginationState?: Partial<PaginationState>;
  dataTestId?: string;
  tableContainerClassName?: string;
  tableClassName?: string;
  summary?: ReactNode;
  skipPageReset?: boolean; // New prop to control auto page reset
}

export function DataTable<TData, TValue>({
  columns,
  data,
  paginate,
  paginationState,
  dataTestId = 'data-table',
  tableContainerClassName,
  tableClassName,
  summary,
  skipPageReset = false, // Default to false (normal behavior)
}: DataTableProps<TData, TValue>) {
  const defaultPageSize = import.meta.env.VITE_DEFAULT_PAGINATION_COUNT
    ? parseInt(import.meta.env.VITE_DEFAULT_PAGINATION_COUNT, 10)
    : 10;

  // Determine if pagination should be applied
  const shouldPaginate = paginate ?? data.length > defaultPageSize;

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    initialState: {
      pagination:
        shouldPaginate && paginationState
          ? paginationState
          : shouldPaginate
            ? { pageSize: defaultPageSize }
            : { pageSize: data.length },
    },
    autoResetPageIndex: !skipPageReset, // Disable auto-resetting when skipPageReset is true
  });

  useEffect(() => {
    if (shouldPaginate) {
      table.setPageSize(defaultPageSize);
    } else {
      table.setPageSize(data.length);
    }
  }, [shouldPaginate, defaultPageSize, table, data]);

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex-grow overflow-auto rounded-md border bg-list">
        <Table
          data-testid={dataTestId}
          containerClassName={cn('h-full', tableContainerClassName)}
          className={tableClassName}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody data-testid={`${dataTestId}-body`}>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  data-testid={`${dataTestId}-row`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow data-testid={`${dataTestId}-row`}>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {!shouldPaginate && summary}
      {shouldPaginate && (
        <div
          className={cn('flex items-center py-4', {
            'justify-between': !!summary,
            'justify-end': !summary,
          })}
        >
          {summary}
          <div className="flex items-center justify-end space-x-2">
            {table.getPageCount() > 1 && (
              <div className="flex w-[100px] items-center justify-center text-sm font-medium italic">
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
